// Typed error model for the shared API client.
// See react-rewrite-plan.md sections 6.5, 8.1, 8.2 and 11.3 api-client.
//
// Security: error context (devMessage / cause) must never contain auth
// header values. The redactHeaders helper here is conservative — anything
// that looks secret-shaped is stripped before being attached to a logged or
// thrown error.

export interface ApiErrorContext {
  // Snapshot of request inputs that is safe to log. Auth-shaped headers are
  // stripped by redactHeaders before being stored here.
  requestId?: string;
  status?: number;
  url?: string;
  method?: string;
  headers?: Record<string, string>;
  body?: unknown;
}

export interface ApiErrorOptions {
  userMessage: string;
  devMessage: string;
  requestId?: string;
  retryable?: boolean;
  cause?: unknown;
  context?: ApiErrorContext;
}

const SECRET_HEADER_PATTERN =
  /^(authorization|cookie|set-cookie|x-api-key|x-auth-token|proxy-authorization)$/i;

export function redactHeaders(
  headers: Record<string, string> | undefined,
): Record<string, string> | undefined {
  if (headers === undefined) {
    return undefined;
  }
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(headers)) {
    if (SECRET_HEADER_PATTERN.test(key)) {
      out[key] = '[redacted]';
      continue;
    }
    out[key] = value;
  }
  return out;
}

export class ApiError extends Error {
  readonly userMessage: string;
  readonly devMessage: string;
  readonly requestId: string | undefined;
  readonly retryable: boolean;
  readonly context: ApiErrorContext | undefined;

  constructor(opts: ApiErrorOptions) {
    super(opts.devMessage);
    this.name = 'ApiError';
    this.userMessage = opts.userMessage;
    this.devMessage = opts.devMessage;
    this.requestId = opts.requestId;
    this.retryable = opts.retryable ?? false;
    this.context = redactErrorContext(opts.context);
    if (opts.cause !== undefined) {
      (this as { cause?: unknown }).cause = opts.cause;
    }
  }
}

export class AuthError extends ApiError {
  constructor(opts: ApiErrorOptions) {
    super(opts);
    this.name = 'AuthError';
  }
}

export class NetworkError extends ApiError {
  constructor(opts: ApiErrorOptions) {
    super({ retryable: true, ...opts });
    this.name = 'NetworkError';
  }
}

export class TimeoutError extends ApiError {
  constructor(opts: ApiErrorOptions) {
    super({ retryable: true, ...opts });
    this.name = 'TimeoutError';
  }
}

export class ConflictError extends ApiError {
  constructor(opts: ApiErrorOptions) {
    super(opts);
    this.name = 'ConflictError';
  }
}

function redactErrorContext(
  context: ApiErrorContext | undefined,
): ApiErrorContext | undefined {
  if (context === undefined) {
    return undefined;
  }
  return { ...context, headers: redactHeaders(context.headers) };
}

// Normalize an unknown thrown value into a typed ApiError subclass. Callers
// provide context (status, url, etc.) when known so the resulting error
// carries useful debugging info without leaking secrets.
export function normalizeError(
  err: unknown,
  context?: ApiErrorContext,
): ApiError {
  if (err instanceof ApiError) {
    return err;
  }
  if (err instanceof Error && err.name === 'AbortError') {
    return new TimeoutError({
      userMessage: 'The request timed out. Please try again.',
      devMessage: err.message || 'Request aborted',
      requestId: context?.requestId,
      cause: err,
      context,
    });
  }
  if (err instanceof Error) {
    return new NetworkError({
      userMessage: 'We could not reach the server. Please try again.',
      devMessage: err.message,
      requestId: context?.requestId,
      cause: err,
      context,
    });
  }
  return new ApiError({
    userMessage: 'Something went wrong on our end. Please try again.',
    devMessage: String(err),
    requestId: context?.requestId,
    context,
  });
}

// The one function UI code should call to put an error in front of a user.
// Never render `error.message`/`String(error)` directly — for an ApiError,
// `.message` is `devMessage` (raw backend/network text), not the curated
// `userMessage`. This always returns the safe, Spectrum-voiced string:
// already-normalized ApiErrors keep their own userMessage, anything else
// (a thrown string, a plain JS error, etc.) gets normalizeError's generic
// fallback rather than whatever it happened to say.
export function getUserErrorMessage(err: unknown): string {
  return normalizeError(err).userMessage;
}
